import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, Database, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import apiClient from '../../config/api.config';

interface AnneeItem { libelle: string; active: boolean; schema: string; schema_existe: boolean; }
interface PreviewEl { source: number; cible: number; }
interface PreviewResp {
  schema_source_ok: boolean; schema_cible_ok: boolean;
  elements: Record<string, PreviewEl>;
}

// Éléments copiables (par année). Les pôles/parcours/options/classes sont PARTAGÉS
// (schéma config) → déjà disponibles dans toute nouvelle année, aucune copie.
const ELEMENTS: { key: string; label: string; desc: string; dep?: string[] }[] = [
  { key: 'formateurs', label: 'Formateurs', desc: 'Enseignants (identité, contacts, spécialité)' },
  { key: 'ue', label: 'Unités d’Enseignement (UE)', desc: 'Maquette : UE par option / niveau / semestre' },
  { key: 'matieres', label: 'Matières', desc: 'Modules rattachés aux UE', dep: ['ue'] },
  { key: 'attributions', label: 'Attributions formateur ↔ matière', desc: 'Qui enseigne quoi', dep: ['matieres', 'formateurs'] },
];
const PARTAGES = ['Pôles', 'Parcours', 'Options', 'Classes'];

const MigrationAnnee: React.FC = () => {
  const [annees, setAnnees] = useState<AnneeItem[]>([]);
  const [source, setSource] = useState('');
  const [cible, setCible] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>({
    formateurs: true, ue: true, matieres: true, attributions: true,
  });
  const [preview, setPreview] = useState<PreviewResp | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [resultats, setResultats] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    apiClient.get('/migration-reference/annees/').then((r) => {
      const list: AnneeItem[] = r.data.annees || [];
      setAnnees(list);
      const active = list.find((a) => a.active);
      if (active) setSource(active.libelle);
      // cible par défaut : année dont le schéma existe mais la plus récente non-active
      const cibleDef = list.find((a) => !a.active && a.schema_existe) || list.find((a) => !a.active);
      if (cibleDef) setCible(cibleDef.libelle);
    }).catch(() => setMessage({ type: 'error', text: 'Chargement des années impossible.' }));
  }, []);

  useEffect(() => {
    if (!source || !cible || source === cible) { setPreview(null); return; }
    setLoadingPreview(true); setResultats(null); setMessage(null);
    apiClient.get('/migration-reference/preview/', { params: { source, cible } })
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null))
      .finally(() => setLoadingPreview(false));
  }, [source, cible]);

  const cibleItem = annees.find((a) => a.libelle === cible);
  const schemaCibleManquant = cibleItem && !cibleItem.schema_existe;

  // applique les dépendances : cocher matieres coche ue ; attributions coche matieres+formateurs
  const toggle = (key: string) => {
    setChecked((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (next[key]) {
        const el = ELEMENTS.find((e) => e.key === key);
        el?.dep?.forEach((d) => { next[d] = true; });
      } else {
        // décocher un prérequis décoche ses dépendants
        ELEMENTS.forEach((e) => { if (e.dep?.includes(key)) next[e.key] = false; });
      }
      return next;
    });
  };

  const selected = useMemo(() => ELEMENTS.filter((e) => checked[e.key]).map((e) => e.key), [checked]);

  const lancer = async () => {
    if (!source || !cible) return;
    if (source === cible) { setMessage({ type: 'error', text: 'Source et cible identiques.' }); return; }
    if (selected.length === 0) { setMessage({ type: 'error', text: 'Cochez au moins un élément.' }); return; }
    if (!window.confirm(`Migrer ${selected.length} type(s) d'éléments de ${source} vers ${cible} ?`)) return;
    setMigrating(true); setMessage(null); setResultats(null);
    try {
      const r = await apiClient.post('/migration-reference/migrer/', { source, cible, elements: selected });
      setResultats(r.data.resultats || {});
      setMessage({ type: 'success', text: `Migration terminée : ${source} → ${cible}.` });
      // rafraîchir l'aperçu
      const p = await apiClient.get('/migration-reference/preview/', { params: { source, cible } });
      setPreview(p.data);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Échec de la migration.' });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Database size={26} /> Migration vers une nouvelle année
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Copier les données de référence (formateurs, UE, matières, attributions) d'une année vers une autre.
          Les pôles, parcours, options et classes sont partagés — déjà disponibles.
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
          message.type === 'success'
            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Sélecteurs source / cible */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5 mb-5">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Année source</label>
            <select value={source} onChange={(e) => setSource(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm min-w-[160px]">
              {annees.map((a) => <option key={a.libelle} value={a.libelle}>{a.libelle}{a.active ? ' (active)' : ''}</option>)}
            </select>
          </div>
          <ArrowRight className="mb-2 text-gray-400" size={22} />
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Année cible</label>
            <select value={cible} onChange={(e) => setCible(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm min-w-[160px]">
              {annees.map((a) => <option key={a.libelle} value={a.libelle}>{a.libelle}{a.active ? ' (active)' : ''}</option>)}
            </select>
          </div>
        </div>
        {source === cible && <p className="text-xs text-red-600 mt-2">Choisissez deux années différentes.</p>}
        {schemaCibleManquant && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            Le schéma de l'année cible n'existe pas encore — il doit être provisionné avant la migration.
          </p>
        )}
      </div>

      {/* Éléments à migrer */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5 mb-5">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">Éléments à migrer</h2>
        <div className="space-y-2">
          {ELEMENTS.map((el) => {
            const pv = preview?.elements?.[el.key];
            return (
              <label key={el.key} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer">
                <input type="checkbox" checked={!!checked[el.key]} onChange={() => toggle(el.key)}
                  className="w-4 h-4 accent-blue-600" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{el.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{el.desc}</div>
                </div>
                {pv && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-right whitespace-nowrap">
                    source : <strong>{pv.source}</strong><br />cible : <strong>{pv.cible}</strong>
                  </div>
                )}
              </label>
            );
          })}
        </div>
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-2">
          Partagés (aucune copie nécessaire) : {PARTAGES.join(', ')}.
        </div>
        {loadingPreview && <div className="mt-2 text-xs text-gray-400 flex items-center gap-1"><Loader2 size={14} className="animate-spin" /> Analyse…</div>}
      </div>

      {/* Résultats */}
      {resultats && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-5 mb-5">
          <h3 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-2">
            <Check size={16} /> Éléments copiés
          </h3>
          <ul className="text-sm text-green-900 dark:text-green-200 space-y-1">
            {Object.entries(resultats).map(([k, v]) => {
              const el = ELEMENTS.find((e) => e.key === k);
              return <li key={k}>{el?.label || k} : <strong>{v}</strong> ajouté(s)</li>;
            })}
          </ul>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={lancer}
          disabled={migrating || !source || !cible || source === cible || selected.length === 0 || !!schemaCibleManquant}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors">
          {migrating ? <Loader2 size={16} className="animate-spin" /> : <Database size={16} />}
          {migrating ? 'Migration en cours…' : `Migrer vers ${cible || '…'}`}
        </button>
      </div>
    </div>
  );
};

export default MigrationAnnee;
